import { InstrumentId } from '../types/assessment'
import {
  AnalysisLine,
  GATED_ON,
  HIGHLIGHT_PHRASES,
  KeyRow,
  PACKAGE_HASH,
  PipelineStage,
  RationaleRow,
  ReconstructionEvent,
  RecordScenarioRow,
  SOURCE_ID,
  ScenarioSlot,
  analysisLines,
  assessmentKey,
  defaultSlots,
  observedBehaviour,
  packLevelItems,
  pipelineStages,
  reconstruction,
  recordScenarioSchedule,
  samplePosting,
  selectionRationale,
} from './demo'
import {
  LAW_GATED_ON,
  LAW_HIGHLIGHT_PHRASES,
  LAW_PACKAGE_HASH,
  LAW_SOURCE_ID,
  lawAnalysisLines,
  lawAssessmentKey,
  lawObservedBehaviour,
  lawPackLevelItems,
  lawPipelineStages,
  lawReconstruction,
  lawRecordScenarioSchedule,
  lawSamplePosting,
  lawSelectionRationale,
  lawSlots,
} from './demoLaw'

// One demo bundle per instrument in the library. Every demo screen reads the
// bundle for the resolved instrument rather than importing FIN content directly.
export interface DemoBundle {
  instrument: InstrumentId
  label: string
  libraryMatch: string
  roleLabel: string
  matterLabel: string
  representativeLabel: string
  sourceId: string
  packageHash: string
  gatedOn: string
  posting: { title: string; url: string; meta: string; paragraphs: string[] }
  highlightPhrases: string[]
  analysisLines: AnalysisLine[]
  slots: ScenarioSlot[]
  assessmentKey: KeyRow[]
  packLevelItems: Omit<KeyRow, 'sc' | 'klass'>[]
  selectionRationale: RationaleRow[]
  pipelineStages: PipelineStage[]
  recordScenarioSchedule: RecordScenarioRow[]
  reconstruction: ReconstructionEvent[]
  observedBehaviour: { heading: string; body: string }[]
  recordSubtitle: string
  consistencyNote: string
}

const FIN_BUNDLE: DemoBundle = {
  instrument: 'FIN-001',
  label: 'FIN-001 v0.1',
  libraryMatch: 'FIN-001 · Financial close',
  roleLabel: 'Financial Controller',
  matterLabel: 'Meridian SAS · Q2 2026 close',
  representativeLabel: 'Representative posting · Financial Controller',
  sourceId: SOURCE_ID,
  packageHash: PACKAGE_HASH,
  gatedOn: GATED_ON,
  posting: samplePosting,
  highlightPhrases: HIGHLIGHT_PHRASES,
  analysisLines,
  slots: defaultSlots,
  assessmentKey,
  packLevelItems,
  selectionRationale,
  pipelineStages,
  recordScenarioSchedule,
  reconstruction,
  observedBehaviour,
  recordSubtitle:
    'Meridian SAS Q2 2026 close · submitted in 47m 12s of 60m · 9 prompts to the assistant · 5 documents opened of 7',
  consistencyNote: 'Below 80% consistency, a scenario is rebuilt. This one held at 90.',
}

const LAW_BUNDLE: DemoBundle = {
  instrument: 'LAW-001',
  label: 'LAW-001 v0.1',
  libraryMatch: 'LAW-001 · Corporate document review · trainee level',
  roleLabel: 'Trainee Solicitor',
  matterLabel: 'Project Harrow · pre-completion review',
  representativeLabel: 'Representative posting · Trainee Solicitor',
  sourceId: LAW_SOURCE_ID,
  packageHash: LAW_PACKAGE_HASH,
  gatedOn: LAW_GATED_ON,
  posting: lawSamplePosting,
  highlightPhrases: LAW_HIGHLIGHT_PHRASES,
  analysisLines: lawAnalysisLines,
  slots: lawSlots,
  assessmentKey: lawAssessmentKey,
  packLevelItems: lawPackLevelItems,
  selectionRationale: lawSelectionRationale,
  pipelineStages: lawPipelineStages,
  recordScenarioSchedule: lawRecordScenarioSchedule,
  reconstruction: lawReconstruction,
  observedBehaviour: lawObservedBehaviour,
  recordSubtitle:
    'Project Harrow pre-completion review · submitted in 52m 04s of 60m · 7 prompts to the assistant · 4 documents shared with the assistant',
  consistencyNote: 'Below 80% consistency, a scenario is rebuilt. This one held at 90.',
}

export const demoBundles: Record<InstrumentId, DemoBundle> = {
  'FIN-001': FIN_BUNDLE,
  'LAW-001': LAW_BUNDLE,
}

export function getDemoBundle(id: InstrumentId): DemoBundle {
  return demoBundles[id] || FIN_BUNDLE
}

// The candidate build for an instrument. One place to repoint.
export function assessmentUrlFor(id: InstrumentId) {
  return id === 'FIN-001' ? '/consent' : `/consent?i=${id}`
}
